import { makeStyles, DropdownSelect, Grid, InputField, Typography, useClientOptions } from '@paintscout/ui'
import type { DropdownSelectOption } from '@paintscout/ui'
import { getDetails, setDetails } from '@paintscout/util/builder'
import { QuoteContext } from '@paintscout/react-quote'
import type { QuoteDocument, QuoteTaxRate } from 'paintscout'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import classNames from 'classnames'
import DetailsItem from './DetailsItem'

const useStyles = makeStyles((theme) => ({
  marginTop: {
    marginTop: theme.spacing(0.25)
  },
  marginBottom: {
    marginBottom: theme.spacing(0.5)
  },
  customTaxRate: {
    padding: theme.spacing(2),
    '& > *': {
      marginBottom: theme.spacing(2)
    }
  },
  textPrimary: {
    color: theme.palette.text.primary
  }
}))

interface TaxItemProps {
  onChange: (args: { quote: QuoteDocument }) => void
  disabled: boolean
  expanded: boolean
  onExpand: (expanded: boolean) => void
}

export function TaxItem(props: TaxItemProps) {
  const { onChange, disabled, expanded, onExpand } = props
  const { quote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const classes = useStyles()
  const allowCustomTaxRate = options.options?.taxRate?.allow_custom ?? false
  const [details, setStateDetails] = useState(getDetails({ quote, options, taxFallback: true }))

  const taxRates = [...(options.options.taxRates ?? ([] as any))]

  const taxRateOptions = taxRates.map((item: { key: string; rate: number; description: string }) => {
    return {
      key: item.key,
      value: item.rate,
      label: [item.description ? item.description : 'Tax', item.rate ? `(${item.rate}%)` : '']
        .filter((item) => item)
        .join(' ')
    }
  })

  const sublabel = `(${details.tax.rate}%)`

  const label = `Tax${details.tax.rate ? ' ' + sublabel : ''}`

  useEffect(() => {
    setStateDetails(getDetails({ quote, options }))
  }, [quote])

  // Setup dropdown select
  const dropdownOptions: DropdownSelectOption[] = [
    ...taxRateOptions,
    ...(allowCustomTaxRate ? [{ key: 'custom', value: 0, label: 'Custom Rate' }] : [])
  ]

  // recalculate quote details and rate
  const updateDetails = (taxDetails: {
    rate: number
    description: string
    type: string
    total: number
    details: QuoteTaxRate[]
  }) => {
    let newRate = parseFloat(taxDetails.details?.[0]?.rate as unknown as string)
    let newDetails = taxDetails.details?.[0]?.description
    taxDetails.details.forEach((rate, i) => {
      if (i > 0) {
        newRate += parseFloat(rate.rate as unknown as string)
        newDetails += `, ${rate.description}`
      }
    })
    return [newRate, newDetails]
  }

  const handleTaxChange = (taxRates: DropdownSelectOption[]) => {
    const existingCustom = details.tax.details?.find((detail) => detail.key === 'custom')
    if (taxRates.find((option) => option.key === 'custom' && !existingCustom)) {
      const customOption = dropdownOptions.find((option) => option.key === 'custom')
      details.tax.details = [
        {
          key: customOption?.key,
          rate: customOption?.value,
          description: customOption?.label
        }
      ]
    } else {
      details.tax.details = taxRates
        .map((option) => {
          return {
            key: option.key,
            rate: option.value,
            // remove rate from description
            // eslint-disable-next-line security/detect-unsafe-regex
            description: option.label.replace(/\s\(\d+(\.\d+)?%\)/, '')
          }
        })
        .filter((option) => option.key !== 'custom')
    }

    const [newRate, newDescription] = updateDetails(details.tax)
    const updatedTaxDetails = {
      ...details.tax,
      rate: newRate as number,
      description: newDescription as string
    }

    const updatedDetails = {
      ...details,
      tax: {
        ...updatedTaxDetails
      }
    }
    setStateDetails(updatedDetails)
    onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }

  const isCustom = (details.tax?.details ?? []).length === 1 && details.tax.details?.[0]?.key === 'custom'

  const CustomTaxRateForm = useCallback(() => {
    const handleCustomTaxChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = ev.target
      details.tax.details[0] = {
        ...details.tax.details?.[0],
        [name]: value,
        key: 'custom'
      }
      const [newRate, newDescription] = updateDetails(details.tax)
      const updatedTaxDetails = {
        ...details.tax,
        rate: newRate as number,
        description: newDescription as string
      }
      const updatedDetails = {
        ...details,
        tax: { ...updatedTaxDetails }
      }
      setStateDetails(updatedDetails)
      onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
    }

    return (
      <>
        <Grid item>
          <InputField
            label={'Tax Description'}
            name="description"
            onChange={handleCustomTaxChange}
            value={details.tax.details?.[0]?.description}
          />
        </Grid>
        {options.options.features?.quotes?.splitTaxRate?.enabled ? (
          <>
            <Grid item>
              <InputField
                label={'Tax Rate (Mats)'}
                name="materialRate"
                onChange={handleCustomTaxChange}
                value={details.tax.details?.[0]?.materialRate}
                format="taxRate"
              />
            </Grid>
            <Grid item>
              <InputField
                label={'Tax Rate (Labor)'}
                name="laborRate"
                onChange={handleCustomTaxChange}
                value={details.tax.details?.[0]?.laborRate}
                format="taxRate"
              />
            </Grid>
          </>
        ) : (
          <Grid item>
            <InputField
              label={'Tax Rate'}
              name="rate"
              format="taxRate"
              onChange={handleCustomTaxChange}
              value={details.tax.details?.[0]?.rate}
            />
          </Grid>
        )}
      </>
    )
  }, [details.tax.details])

  // Check if missing taxes are present to show warning
  const missingTaxes = details.tax.details
    ? details.tax.details.filter(
        (appliedTax) => !taxRateOptions.find((availableTax) => availableTax.key === appliedTax.key)
      )
    : []

  function warningMessage({ quote }: { quote: QuoteDocument }): {
    text: string
    link?: { text: string; id: string }
  } {
    const isTemplate = quote.type === 'quoteTemplate'
    return {
      text: `This tax rate has been deleted in your settings.`,
      link: !isTemplate && quote.fromTemplate?.key ? { text: 'Update Template', id: quote.fromTemplate.key } : undefined
    }
  }
  return (
    <DetailsItem
      label={label}
      value={details.tax.total}
      expanded={expanded}
      onExpand={onExpand}
      warningMessage={missingTaxes.length > 0 ? warningMessage({ quote }) : null}
      detailsComponent={
        <Grid
          className={classNames({
            [classes.marginBottom]: true,
            [classes.marginTop]: expanded && missingTaxes.length > 0
          })}
          container
          direction="row"
          justifyContent="flex-end"
          spacing={2}
        >
          <Grid item xs={12}>
            <DropdownSelect
              searchable
              label={'Select Rate'}
              options={dropdownOptions}
              variant="multi"
              placeholder={{
                plural: 'Tax Rates'
              }}
              value={dropdownOptions.filter((option) =>
                details.tax.details?.some((detail) => detail.key === option.key)
              )}
              disabled={!allowCustomTaxRate || disabled}
              onChange={handleTaxChange}
              formatOptionLabel={(_, placement) => {
                if (placement !== 'selected') return
                const selectedDetails = details.tax.details ?? []
                if (!selectedDetails.length) {
                  return <Typography className={classes.textPrimary}>No Tax</Typography>
                }
                if (isCustom) {
                  return <Typography className={classes.textPrimary}>{selectedDetails[0]?.description}</Typography>
                }
                const selectedLabel =
                  selectedDetails.length === 1 ? selectedDetails[0]?.description : `${selectedDetails.length} selected`
                return <Typography className={classes.textPrimary}>{selectedLabel}</Typography>
              }}
              pagination={{
                currentPage: isCustom && allowCustomTaxRate ? 1 : 0,
                pages:
                  allowCustomTaxRate || isCustom
                    ? [
                        <Grid container className={classes.customTaxRate} key={'custom-tax-rate'}>
                          <CustomTaxRateForm />
                        </Grid>
                      ]
                    : [],
                onPageChange: (page) => {
                  if (page === 0) {
                    details.tax.details = details.tax.details?.filter((detail) => detail.key !== 'custom')
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      }
    />
  )
}
